import React, { useCallback } from "react";
import { ActivityTemplateEditableFields } from "../api/contracts";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { ActivityTemplateForm } from "../components/ActivityTemplateForm";
import { fileToBase64 } from "../components/ActivityImages";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { activityTemplateDetailPath } from "../config/navigation";
import {
  activityTemplatesReturnToFromSearch,
  withActivityTemplatesReturnTo,
} from "../config/activityTemplateNavigation";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";

export function EditActivityTemplatePage({
  clinicianCode,
  templateId,
}: {
  clinicianCode: string;
  templateId: string;
}) {
  const request = useCallback(
    async (signal: AbortSignal) => {
      const [templateData, activityTypeData] = await Promise.all([
        clinicianPortalApi.getActivityTemplate(
          clinicianCode,
          templateId,
          signal,
        ),
        clinicianPortalApi.getActivityTypes(clinicianCode, signal),
      ]);
      return { templateData, activityTypeData };
    },
    [clinicianCode, templateId],
  );
  const state = usePortalData(request);
  const mutation = usePortalMutation();
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const templateData = data?.templateData;
  const activityTypeData = data?.activityTypeData;
  const available = Boolean(
    templateData?.success &&
      templateData.found &&
      templateData.authorized &&
      templateData.therapist &&
      templateData.template &&
      activityTypeData?.found &&
      activityTypeData.authorized &&
      activityTypeData.therapist,
  );
  const template = available ? templateData?.template : null;
  const detailPath = activityTemplateDetailPath(templateId);
  const returnTo = activityTemplatesReturnToFromSearch(window.location.search);
  const detailPathWithReturnTo = withActivityTemplatesReturnTo(
    detailPath,
    returnTo,
  );
  const initialValues: ActivityTemplateEditableFields | undefined = template
    ? {
        name: template.name,
        description: template.description,
        activityTypeId: template.activityTypeId,
        status: template.status,
        nonExposureHomework: template.nonExposureHomework,
        activitySteps: template.activitySteps,
        alternative: template.alternative,
        coachingPrompts: template.coachingPrompts,
        points: template.points,
        coins: template.coins,
      }
    : undefined;

  return (
    <ClinicianPortalLayout
      activeGlobalKey="templates"
      clinicianCode={clinicianCode}
      therapistRole={templateData?.therapist?.role}
    >
      <PortalHeader
        title={template?.name || "Edit Activity Template"}
        clinicianName={
          available ? templateData?.therapist?.therapistName : undefined
        }
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              {
                label: "Activity Templates",
                path: returnTo,
              },
              {
                label: template?.name || "Template Detail",
                path: detailPathWithReturnTo,
              },
              { label: "Edit Activity Template" },
            ]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--profile">
        {state.status === "loading" ? (
          <LoadingState label="Loading Activity Template form" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState
            error={
              new Error(
                templateData?.error ||
                  "Activity Template editing is unavailable",
              )
            }
          />
        ) : null}
        {available && template && initialValues && activityTypeData ? (
          <ActivityTemplateForm
            key={template.id}
            mode="edit"
            initialValues={initialValues}
            activityTypes={activityTypeData.activityTypes}
            clinicianCode={clinicianCode}
            cancelPath={returnTo}
            isSubmitting={mutation.state.status === "pending"}
            submissionError={
              mutation.state.status === "error"
                ? mutation.state.error
                : undefined
            }
            clearSubmissionError={mutation.reset}
            template={template}
            onUploadImage={async (file) => {
              const result =
                await clinicianPortalApi.uploadActivityTemplateImage(
                  clinicianCode,
                  templateId,
                  {
                    filename: file.name,
                    mimeType: file.type,
                    sizeBytes: file.size,
                    contentBase64: await fileToBase64(file),
                  },
                );
              if (!result.uploaded || !result.image) {
                throw new Error(
                  result.error || "The image could not be uploaded.",
                );
              }
              return result.image;
            }}
            onRemoveImage={async (imageId) => {
              const result =
                await clinicianPortalApi.deleteActivityTemplateImage(
                  clinicianCode,
                  templateId,
                  imageId,
                );
              if (!result.deleted) {
                throw new Error(
                  result.error || "The image could not be removed.",
                );
              }
            }}
            onSubmit={async (values) => {
              const result = await mutation.run(
                "update-activity-template",
                (signal) =>
                  clinicianPortalApi.updateActivityTemplate(
                    clinicianCode,
                    templateId,
                    values,
                    signal,
                  ),
              );
              if (result?.updated && result.template) {
                navigateWithinPortal(
                  withClinicianCode(returnTo, clinicianCode),
                );
              }
            }}
          />
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
